/**
 * Names for the ids a filter URL carries (places, residences, developers,
 * amenity tags), loaded once per page and shared: the search bar's chips
 * and the applied-filter chips above the results read the same copy, so
 * the four requests go out once (moved out of MagicSearchBar).
 */
import {
  fetchListingsLocations, fetchPlaceNames, fetchResidenceNames, getDevelopers, getFeatureTags, type FeatureTagVocab,
} from "@/lib/api";
import { bindPlaceNames, rememberPlaceNames, urlPlaceIds } from "@/lib/place-names";
import type { LocationsResponse } from "@/types";

// The idle panel's "Populyar yerlər": Baku's busiest rayons by active
// homes (production, 2026-10-21: Yasamal 23.7k, Xətai 22k, Nərimanov 16.1k,
// Abşeron 14.8k, Nəsimi 13.3k, Binəqədi 12.1k, Sabunçu 11.9k, Xəzər 9.2k).
// The taxonomy's rayon group also holds towns outside Baku (Ağdam, Astara,
// Gədəbəy) where nothing is listed; taking its first eight alphabetically
// offered them as "popular" (owner, 2026-10-21).
const POPULAR_RAYON_IDS = ["56", "16", "10", "197", "190", "58", "13", "15"];

export interface FilterNames {
  loc: Record<string, string>;
  res: Record<string, string>;
  dev: Record<string, string>;
  vocab: FeatureTagVocab;
  rayons: { id: string; name: string }[];
}

let _names: FilterNames | null = null;
// The first load in flight, shared: opening the panel, the sheet or a URL
// change before it resolved each fired the same four requests again.
let _namesP: Promise<FilterNames> | null = null;

/** The names, if they are loaded already (no request). */
export function cachedFilterNames(): FilterNames | null {
  return _names;
}

export function loadFilterNames(): Promise<FilterNames> {
  if (_names) return Promise.resolve(_names);
  if (!_namesP) _namesP = readNames().finally(() => { _namesP = null; });
  return _namesP;
}

async function readNames(): Promise<FilterNames> {
  const [locs, residencesResp, vocab, developers] = await Promise.all([
    fetchListingsLocations().catch(() => null),
    fetchResidenceNames().catch(() => ({ residences: [] })),
    getFeatureTags().catch(() => ({ tags: [], popular: [] })),
    getDevelopers(),
  ]);
  const residences = residencesResp.residences ?? [];
  const loc: Record<string, string> = {};
  let rayons: { id: string; name: string }[] = [];
  for (const g of (locs as LocationsResponse | null)?.groups ?? []) {
    for (const l of g.locations) loc[l.id] = l.name;
    if (g.type === "rayon" && rayons.length === 0) {
      const byId = new Map(g.locations.map((l) => [l.id, l]));
      rayons = POPULAR_RAYON_IDS.map((id) => byId.get(id))
        .filter((l): l is NonNullable<typeof l> => !!l)
        .map((l) => ({ id: l.id, name: l.name }));
    }
  }
  // Schools, kindergartens and chains the search read ("p-..."): the
  // bar's answers, and a link's own, fetched once.
  bindPlaceNames(loc);
  const wanted = urlPlaceIds().filter((id) => !(id in loc));
  if (wanted.length) rememberPlaceNames((await fetchPlaceNames(wanted).catch(() => ({ names: {} }))).names);
  const res: Record<string, string> = {};
  for (const r of residences) res[r.cluster_id] = r.name;
  const dev: Record<string, string> = {};
  for (const d of developers) dev[d.slug] = d.name;
  _names = { loc, res, dev, vocab, rayons };
  return _names;
}
